import React from 'react';
import { graphql } from 'gatsby';
import {
  Section,
  Box,
  Text,
  Link,
  IntrinsicImage,
  Flex,
  SanityRichText,
  type TishmanThemeName,
} from '@tishman/components';
import { Block } from '@sanity/block-content-to-react';

import { Layout } from '../../layouts';
import { PrivateEventsNews } from '../../blocks/PrivateEvents/PrivateEventsNews';
import { PrivateEventsHeroTwoColumn } from '../../blocks/PrivateEvents/Hero/PrivateEventsHeroTwoColumn';
import {
  ICard,
  PrivateDiningAndVenuesGrid,
} from '../../components/DiningAndVenuesGrid/DiningAndVenuesGrid';

import type { PageConfig } from '../../PageConfig';

export const pageConfig: PageConfig = {
  theme: 'Rock Center Event Type',
  pageName: 'Private Events',
  logo: 'Rockefeller Center',
  cta: { to: '/contact/private-events/', label: 'Plan an Event' },
};

const SanityEventTypeTemplate = ({
  data,
}: {
  data: {
    sanityEventTypeTemplate: Queries.SanityEventTypeTemplate;
  };
}) => {
  const { sanityEventTypeTemplate } = data;

  if (!sanityEventTypeTemplate)
    throw new Error('Expected valid sanityEventTypeTemplate data');

  const heroImage = sanityEventTypeTemplate.heroImage;
  if (!heroImage || !heroImage.asset?.gatsbyImageData)
    throw new Error('Expected valid heroImage data');

  const heroCta = sanityEventTypeTemplate.heroCta;
  if (!heroCta) throw new Error('Expected valid heroCta data');

  const heroEyebrow = sanityEventTypeTemplate.heroEyebrow;
  if (!heroEyebrow) throw new Error('Expected valid heroEyebrow data');

  const heroTitle = sanityEventTypeTemplate.heroTitle;
  if (!heroTitle) throw new Error('Expected valid heroTitle data');

  return (
    <Layout>
      <Box sx={{ width: '100%', height: '16px' }} />
      <Section
        id="details"
        sx={{ marginTop: ['24px', 0] }}
        theme="Rock Center White"
      >
        <PrivateEventsHeroTwoColumn
          cta={heroCta}
          eyebrow={heroEyebrow}
          image={heroImage as Queries.SanityImageType}
          pageName={`Event Type Template ${
            sanityEventTypeTemplate.seo?.metaTitle ?? ''
          }`}
          rawHeroLongText={
            (sanityEventTypeTemplate._rawHeroLongText ?? []) as Block[]
          }
          title={heroTitle}
        />
      </Section>
      {sanityEventTypeTemplate?.venueGridToggle && (
        <Section
          sx={{
            mb: '60px',
            maxWidth: '1140px',
            marginLeft: 'auto',
            marginRight: 'auto',
            paddingTop: ['39px', '64px'],
          }}
        >
          <Box sx={{ padding: ['0 16px', '0 24px', '0 32px', '0 32px', 0] }}>
            <PrivateDiningAndVenuesGrid
              cards={
                (sanityEventTypeTemplate.venueGrid?.map((venue) => {
                  return {
                    ...venue,
                    cardTitle: venue?.title,
                    cardText: venue?.description,
                    image: venue?.image,
                  };
                }) ?? []) as ICard[]
              }
              desktopGridColumns={2}
              desktopGridGap="24px 16px"
              showLearnMoreOnMobile
              title={sanityEventTypeTemplate.venueGridTitle ?? ''}
            />
          </Box>
        </Section>
      )}

      {sanityEventTypeTemplate?.calloutsToggle &&
        sanityEventTypeTemplate.callouts?.map((callout, index) => {
          const theme = (callout?.colorSchema ??
            'Rock Center') as TishmanThemeName;
          const linkSx =
            theme === 'Rock Center Dark Gold' ? { color: '#000000' } : {};

          return (
            <Section key={index} theme={theme}>
              <Box>
                <Flex
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: ['24px', '64px'],
                    padding: ['64px 16px', '64px 32px'],
                    flexDirection: [
                      'column-reverse',
                      'column-reverse',
                      index % 2 ? 'row-reverse' : 'row',
                    ],
                    maxWidth: '1200px',
                    margin: '0 auto',
                  }}
                >
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      gap: '30px',
                      flex: 1,
                      flexGrow: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Flex
                      sx={{
                        flexDirection: 'column',
                        gap: '16px',
                        alignSelf: 'stretch',
                        width: '100%',
                        flex: '0 0 auto',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Text
                        sx={{
                          alignSelf: 'stretch',
                          marginTop: '-1px',
                        }}
                        variant="h2"
                      >
                        {callout?.title}
                      </Text>

                      <SanityRichText
                        blocks={
                          (callout?._rawLongText ?? []) as unknown as Block[]
                        }
                        components={{
                          p: ({ children }) => (
                            <Text
                              sx={{
                                fontSize: 3,
                                lineHeight: [1.16, 1.17],
                              }}
                              variant="body"
                            >
                              {children as React.ReactNode}
                            </Text>
                          ),
                        }}
                      />
                    </Flex>

                    {callout?.cta?.url && callout?.cta?.caption && (
                      <Link
                        sx={{ letterSpacing: '2px', ...linkSx }}
                        to={callout?.cta?.url || ''}
                        variant="button"
                      >
                        {callout?.cta?.caption}
                      </Link>
                    )}
                  </Flex>

                  <Flex
                    sx={{
                      flexDirection: 'column',
                      alignItems: index % 2 === 0 ? 'flex-end' : 'flex-start',
                      flex: '1 0 0',
                      width: '100%',
                      maxHeight: '268px',
                    }}
                  >
                    {callout?.image && callout.image.asset?.gatsbyImageData && (
                      <IntrinsicImage
                        alt={callout.image.alt || ''}
                        image={callout.image.asset.gatsbyImageData}
                        sx={{
                          width: '100%',
                          height: '100%',
                          aspectRatio: '16/9',
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              </Box>
            </Section>
          );
        })}
      {sanityEventTypeTemplate?.newsToggle && (
        <PrivateEventsNews
          items={(sanityEventTypeTemplate?.news ?? []) as Queries.SanityStory[]}
          showReadTheMagazineButton={false}
          theme={
            (sanityEventTypeTemplate?.newsColorSchema ??
              'Rock Center Dark Gold') as TishmanThemeName
          }
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query SanityEventTypeTemplate($slug__current: String!) {
    meta: sanityEventTypeTemplate(slug: { current: { eq: $slug__current } }) {
      seo {
        title: metaTitle
        description: metaDescription
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: FIXED
              width: 1200
              formats: [AUTO, WEBP]
              placeholder: BLURRED
            )
          }
        }
      }
    }
    sanityEventTypeTemplate(slug: { current: { eq: $slug__current } }) {
      _id
      seo {
        metaTitle
      }
      heroEyebrow
      heroTitle
      _rawHeroLongText
      heroImage {
        alt
        asset {
          gatsbyImageData(width: 1600, layout: CONSTRAINED)
        }
      }
      heroCta {
        url
        caption
      }
      callouts {
        _key
        title
        colorSchema
        _rawLongText
        image {
          alt
          asset {
            gatsbyImageData(
              width: 800
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
        cta {
          url
          caption
        }
      }
      news {
        id
        category
        titleAndSlug {
          title
          slug {
            current
          }
        }
        publishAt
        poster {
          alt
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
        authors
      }
      newsColorSchema
      venueGrid {
        _id
        title
        description
        cta {
          caption
          url
        }
        image {
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
      }
      venueGridTitle
      venueGridToggle
      calloutsToggle
      newsToggle
    }
  }
`;

export default SanityEventTypeTemplate;
